import { useCallback, useMemo, useSyncExternalStore } from "react";

import { browserSessionStorage } from "@/react/lib/browserStorage";

const STORAGE_KEY = "community-switcher-circle-ai-intro-state";
const CHANGE_EVENT = "community-switcher-circle-ai-intro-change";
const PENDING = "pending";
const SEEN = "seen";

const notifyChange = () => {
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

const subscribe = (onChange: () => void) => {
  window.addEventListener(CHANGE_EVENT, onChange);
  return () => window.removeEventListener(CHANGE_EVENT, onChange);
};

const getStorageKey = (userId?: number) =>
  userId ? `${STORAGE_KEY}:${userId}` : undefined;

export const markCircleAiIntroPending = (userId?: number) => {
  const storageKey = getStorageKey(userId);
  if (!storageKey || browserSessionStorage.getItem(storageKey) === SEEN) return;

  browserSessionStorage.setItem(storageKey, PENDING);
  notifyChange();
};

export const useCircleAiIntroSession = (userId?: number) => {
  const storageKey = getStorageKey(userId);
  const getSnapshot = useCallback(
    () =>
      Boolean(
        storageKey && browserSessionStorage.getItem(storageKey) === PENDING,
      ),
    [storageKey],
  );
  const shouldShowCircleAiIntro = useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => false,
  );
  const onCircleAiIntroShown = useCallback(() => {
    if (!storageKey) return;

    browserSessionStorage.setItem(storageKey, SEEN);
    notifyChange();
  }, [storageKey]);

  return useMemo(
    () => ({ shouldShowCircleAiIntro, onCircleAiIntroShown }),
    [onCircleAiIntroShown, shouldShowCircleAiIntro],
  );
};
